import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Question } from '../types';

interface QuestionGradingProps {
  questionNumber: number;
  onScoreChange: (questionNumber: number, score: string | null, note?: string) => void;
  initialScore?: string | null;
  initialNote?: string;
  readOnly?: boolean;
}

const QuestionContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const QuestionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
`;

const ScoreOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-bottom: 16px;
`;

const ScoreButton = styled.button<{ selected: boolean; disabled?: boolean }>`
  padding: 12px 8px;
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e2e8f0'};
  border-radius: 8px;
  background: ${props => props.selected ? '#dbeafe' : 'white'};
  color: ${props => props.selected ? '#1d4ed8' : '#64748b'};
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    border-color: ${props => props.disabled ? '#e2e8f0' : '#3b82f6'};
    background: ${props => props.disabled ? 'white' : '#f0f9ff'};
  }
`;

const NoteInput = styled.textarea<{ disabled?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  cursor: ${props => props.disabled ? 'not-allowed' : 'text'};
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:focus {
    outline: none;
    border-color: ${props => props.disabled ? '#e2e8f0' : '#3b82f6'};
    box-shadow: ${props => props.disabled ? 'none' : '0 0 0 3px rgba(59, 130, 246, 0.1)'};
  }
`;

const NoteLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const QuestionGrading: React.FC<QuestionGradingProps> = ({
  questionNumber,
  onScoreChange,
  initialScore = null,
  initialNote = '',
  readOnly = false
}) => {
  // İlk açıldığında hiçbir puan seçili olmamalı (null/undefined)
  const [selectedScore, setSelectedScore] = useState<string | null>(initialScore);
  const [note, setNote] = useState(initialNote);

  // initialScore değiştiğinde state'i güncelle
  useEffect(() => {
    setSelectedScore(initialScore);
  }, [initialScore]);

  // initialNote değiştiğinde state'i güncelle
  useEffect(() => {
    setNote(initialNote);
  }, [initialNote]);

  const scoreOptions = [
    { value: '0', label: '0' },
    { value: '1/3', label: '1/3' },
    { value: '1/2', label: '1/2' },
    { value: '2/3', label: '2/3' },
    { value: '1', label: '1' }
  ];

  const handleScoreChange = (score: string) => {
    if (readOnly) return;
    
    // Aynı puana tekrar basılırsa seçimi kaldır
    if (selectedScore === score) {
      setSelectedScore(null);
      onScoreChange(questionNumber, null, note);
    } else {
      setSelectedScore(score);
      onScoreChange(questionNumber, score, note);
    }
  };

  const handleNoteChange = (newNote: string) => {
    if (readOnly) return;
    
    setNote(newNote);
    if (selectedScore !== null) {
      onScoreChange(questionNumber, selectedScore, newNote);
    }
  };

  return (
    <QuestionContainer>
      <QuestionTitle>Sual {questionNumber}</QuestionTitle>
      
      <ScoreOptions>
        {scoreOptions.map((option) => (
          <ScoreButton
            key={option.value}
            selected={selectedScore !== null && selectedScore === option.value}
            disabled={readOnly}
            onClick={() => handleScoreChange(option.value)}
          >
            {option.label}
          </ScoreButton>
        ))}
      </ScoreOptions>

      <NoteLabel htmlFor={`note-${questionNumber}`}>
        Qeyd (İstəyə bağlı):
      </NoteLabel>
      <NoteInput
        id={`note-${questionNumber}`}
        value={note}
        disabled={readOnly}
        onChange={(e) => handleNoteChange(e.target.value)}
        placeholder={readOnly ? "Yalnız baxış rejimi" : "Bu sual haqqında qeyd yaza bilərsiniz..."}
      />
    </QuestionContainer>
  );
};

export default QuestionGrading;
